import React, { useEffect, useState } from 'react'
import { Alert } from '@/components/ui/alert'
import { useDispatch, useSelector } from 'react-redux'

import {
	fetchAllReleaseNotes,
	addReleaseNotes,
	editReleaseNote,
} from './releaseNotes.asyncActions'
import { isEmpty } from 'lodash'
import { selectUser } from '../Profile/profile.selectors'
import {
	selectReleaseNotes,
	selectShouldFetchReleaseNotes,
	selectReleaseNoteModal,
	selectShouldDisableAddButton,
	selectIsReleaseNoteDeleted,
} from './releaseNotes.selectors'
import { actions } from './releaseNotes.slice'
import { RELEASE_NOTES_MODAL_TYPES } from '../ReleaseNotesModal/ReleaseNotesModals.constants'
import ReleaseNoteModal from '../ReleaseNotesModal'
import ReleaseNoteCards from './ReleaseNoteCards'
import PageLayout from '../../components/ui/page-layout'
import { Button } from '../../components/ui/button'

const INITIAL = 0
const emptyString = ''
const timeOutForAlertPrompt = 3000
const successMessage = 'Release Note Saved Successfully.'
const updateMessage = 'Release Note Updated Successfully.'
const errorMessage = 'Please enter valid details to save Release Note.'
const multipleCardsErrorMessage =
	'Please save current Release Note details before adding new Release Note.'

const {
	setShouldFetchReleaseNotes,
	setSelectedReleaseNoteModal,
	setReleaseNoteToBeDeleted,
	setShouldDisableAddButton,
	setIsReleaseNoteDeleted,
} = actions

const ReleaseNotes = () => {
	const dispatch = useDispatch()
	const isReleaseNoteDeleted = useSelector(selectIsReleaseNoteDeleted)
	const savedReleaseNotes = useSelector(selectReleaseNotes)
	const shouldFetchReleaseNotes = useSelector(selectShouldFetchReleaseNotes)
	const shouldDisableAddButton = useSelector(selectShouldDisableAddButton)
	const [newReleaseNotesList, setNewReleaseNotesList] = useState([])
	const [allReleaseNotes, setAllReleaseNotes] = useState([])
	const [areAllCardsValid, setAllCardsAreValid] = useState(true)
	const [shouldShowAlertMessage, setShouldShowAlertMessage] = useState(false)
	const [shouldShowSuccessVariant, setShouldShowSuccessVariant] =
		useState(false)
	const [alertMessage, setAlertMessage] = useState()
	const [isAddReleaseNoteButtonClicked, setIsAddReleaseNoteButtonClicked] =
		useState(false)
	const [releaseNoteToBeEdited, setReleaseNoteToBeEdited] = useState()

	const currentUser = useSelector(selectUser)
	const selectedReleaseNotesModal = useSelector(selectReleaseNoteModal)

	useEffect(() => {
		if (isEmpty(allReleaseNotes) && shouldFetchReleaseNotes) {
			dispatch(fetchAllReleaseNotes()).then(() =>
				dispatch(setShouldDisableAddButton(false))
			)
			dispatch(setShouldFetchReleaseNotes(false)) // TODO: Needs to fix the issue of multiple times render components from Navbar.
		}
	}, [dispatch, allReleaseNotes, shouldFetchReleaseNotes])

	useEffect(() => {
		if (savedReleaseNotes) {
			setAllReleaseNotes(savedReleaseNotes)
		}
	}, [savedReleaseNotes, dispatch])

	const addNewReleaseNote = (currentReleaseNoteCard, indexOfCard) => {
		if (!newReleaseNotesList.length && !releaseNoteToBeEdited) {
			const currentReleaseNote =
				currentReleaseNoteCard || allReleaseNotes[INITIAL]

			const nextReleaseNoteId = currentReleaseNote
				? currentReleaseNoteCard
					? currentReleaseNoteCard.id
					: currentReleaseNote.nextReleaseNoteId
				: null

			let updatedReleaseNotesList = allReleaseNotes

			const newReleaseNote = {
				title: emptyString,
				description: emptyString,
				createdOn: new Date(),
				createdById: currentUser.id,
				isDeleted: false,
				nextReleaseNoteId,
			}

			setAllCardsAreValid(false)
			setNewReleaseNotesList([
				...newReleaseNotesList.slice(INITIAL, indexOfCard),
				newReleaseNote,
				...newReleaseNotesList.slice(indexOfCard),
			])

			updatedReleaseNotesList = [
				...updatedReleaseNotesList.slice(INITIAL, indexOfCard),
				newReleaseNote,
				...updatedReleaseNotesList.slice(indexOfCard),
			]

			setIsAddReleaseNoteButtonClicked(false)
			setAllReleaseNotes(updatedReleaseNotesList)
		} else {
			setShouldShowSuccessVariant(false)
			renderAlertPrompt(multipleCardsErrorMessage)
			window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
		}
	}

	const handleChangeValue = (ev, key, index) => {
		const updatedReleaseNotes = allReleaseNotes.map((each, idx) => {
			if (idx === index) each = { ...each, [key]: ev.target.value }
			return each
		})
		const areAllCardsValid = updatedReleaseNotes.every(
			(each) => !!each.title.trim() && !!each.description.trim()
		)
		setAllCardsAreValid(areAllCardsValid)
		setAllReleaseNotes(updatedReleaseNotes)
	}

	const checkIsInvalidReleaseNote = (value, key) =>
		isEmpty(value) &&
		isAddReleaseNoteButtonClicked && (
			<div className='error-message'>{`Enter valid ${key}.`}</div>
		)

	const handleDeleteReleaseNote = (releaseNote) => {
		setIsAddReleaseNoteButtonClicked(false)
		if (releaseNote.id) {
			dispatch(setReleaseNoteToBeDeleted(releaseNote.id))
			dispatch(
				setSelectedReleaseNoteModal(
					RELEASE_NOTES_MODAL_TYPES.DELETE_RELEASE_NOTE_MODAL
				)
			)
		} else {
			const updateAllReleaseNotesList = allReleaseNotes.filter(
				(each) => each !== releaseNote
			)
			setAllReleaseNotes(updateAllReleaseNotesList)
			setNewReleaseNotesList([])
			setReleaseNoteToBeEdited()
		}
	}

	useEffect(() => {
		if (isReleaseNoteDeleted) {
			setNewReleaseNotesList([])
			setReleaseNoteToBeEdited()
			dispatch(setIsReleaseNoteDeleted(false))
		}
	}, [dispatch, isReleaseNoteDeleted])

	const renderAlertPrompt = (message) => {
		setAlertMessage(message)
		setShouldShowAlertMessage(true)
		setTimeout(() => {
			setShouldShowAlertMessage(false)
		}, timeOutForAlertPrompt)
	}

	const handleAddNewReleaseNote = () => {
		addNewReleaseNote(null, INITIAL)
	}

	const handleSaveNewReleaseNote = (releaseNote) => {
		if (areAllCardsValid) {
			dispatch(setShouldDisableAddButton(true))
			dispatch(addReleaseNotes(releaseNote)).then(() => {
				dispatch(fetchAllReleaseNotes()).then(() => {
					dispatch(setShouldDisableAddButton(false))
				})
				setShouldShowSuccessVariant(true)
				setNewReleaseNotesList([])
				renderAlertPrompt(successMessage)
			})
		} else {
			setShouldShowSuccessVariant(false)
			renderAlertPrompt(errorMessage)
		}
		setIsAddReleaseNoteButtonClicked(!areAllCardsValid)
	}

	const handleUpdateReleaseNote = (releaseNote) => {
		const areAllCardsValid = releaseNote.title && releaseNote.description
		setAllCardsAreValid(areAllCardsValid)
		if (areAllCardsValid) {
			dispatch(setShouldDisableAddButton(true))
			dispatch(editReleaseNote(releaseNote)).then(() => {
				dispatch(fetchAllReleaseNotes()).then(() =>
					dispatch(setShouldDisableAddButton(false))
				)
				renderAlertPrompt(updateMessage)
				setShouldShowSuccessVariant(true)
			})
			setReleaseNoteToBeEdited()
		} else {
			setShouldShowSuccessVariant(false)
			renderAlertPrompt(errorMessage)
		}
		setIsAddReleaseNoteButtonClicked(!areAllCardsValid)
	}

	const handleEditOrSaveReleaseNote = (releaseNote, index) => {
		if (releaseNote.id) {
			setReleaseNoteToBeEdited(releaseNote.id)
		} else handleSaveNewReleaseNote({ ...releaseNote, id: index })
	}

	return (
		<PageLayout title='Release Notes' action={<Button onClick={() => handleAddNewReleaseNote()}>Add New Release Note</Button>}>
		<div className='px-4 mb-100'>
			{selectedReleaseNotesModal && ReleaseNoteModal(selectedReleaseNotesModal)}
			<div className=' mt-30'>
				{shouldShowAlertMessage && (
					<div className='mb-30 max-w-2xl'>
						<Alert className={shouldShowSuccessVariant ? 'border border-green-300 text-green-700 bg-green-50' : 'border border-red-300 text-red-700 bg-red-50'}>
							{alertMessage}
						</Alert>
					</div>
				)}
			</div>
			<ReleaseNoteCards
				allReleaseNotes={allReleaseNotes}
				handleChangeValue={handleChangeValue}
				handleDeleteReleaseNote={handleDeleteReleaseNote}
				checkIsInvalidReleaseNote={checkIsInvalidReleaseNote}
				addNewReleaseNote={addNewReleaseNote}
				releaseNoteToBeEdited={releaseNoteToBeEdited}
				handleUpdateReleaseNote={handleUpdateReleaseNote}
				handleEditOrSaveReleaseNote={handleEditOrSaveReleaseNote}
				newReleaseNotesList={newReleaseNotesList}
				areAllCardsValid={areAllCardsValid}
			/>
		</div>
		</PageLayout>
	)
}

export default ReleaseNotes
